import React, { FC, useCallback, memo } from 'react';
import { useFormikContext } from 'formik';
import _some from 'lodash/some';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _includes from 'lodash/includes';
import { gqlTalentUnionEnum as TALENT_UNION } from '@minecraft.graphql-types';
import SelectableSearchDropdown from '@minecraft.selectable-search-dropdown';
import { useUnionStatusOptions } from '@blocs.filter';
import { useTranslation } from '@blocs.i18n';
import { sagAftraChildrenExclusion } from '../../shared/config';
import { FilterData, SelectOption } from '../../shared/types';
import { shouldNonUnionBeChecked } from '../../shared/utils';
import { Label, StyledSelectableSearchDropdown } from '../../shared/styles';

type Props = {
  unionStatusName: FilterData['name'];
  excludeUnionStatusName: FilterData['name'];
  label: FilterData['label'];
};

const UnionStatus: FC<Props> = ({ unionStatusName, excludeUnionStatusName, label }) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const unionStatusOptions = useUnionStatusOptions();

  const handleUnionStatusChange = useCallback(
    (options: SelectOption[]): void => {
      const isNonUnionChecked = shouldNonUnionBeChecked(options);
      const isSomeSagAftraChildrenExclusionChecked = _some(options, (option) =>
        _includes(sagAftraChildrenExclusion, option.value)
      );
      const nonUnionStatus = _find(unionStatusOptions, { value: TALENT_UNION.NON_UNION });
      const unionStatusOptionsWithoutNonUnionStatus = _filter(
        unionStatusOptions,
        (item) => item.value !== TALENT_UNION.NON_UNION
      );

      if (isNonUnionChecked && isSomeSagAftraChildrenExclusionChecked) {
        const checkedSagAftraChildrenExclusion = _filter(options, (option) =>
          _includes(sagAftraChildrenExclusion, option.value)
        );
        const checkedSagAftraChildrenExclusionValues = _map(checkedSagAftraChildrenExclusion, (option) => option.value);

        setFieldValue(unionStatusName, [nonUnionStatus, ...checkedSagAftraChildrenExclusion], false);
        setFieldValue(
          excludeUnionStatusName,
          _filter(
            unionStatusOptionsWithoutNonUnionStatus,
            (option) => !_includes(checkedSagAftraChildrenExclusionValues, option.value)
          ),
          false
        );
      } else if (isNonUnionChecked) {
        setFieldValue(unionStatusName, [nonUnionStatus], false);
        setFieldValue(excludeUnionStatusName, unionStatusOptionsWithoutNonUnionStatus, false);
      } else {
        setFieldValue(excludeUnionStatusName, [], false);
        setFieldValue(unionStatusName, options, false);
      }
    },
    [values[unionStatusName]]
  );

  return (
    <>
      <Label $subLabel={t('common:filters.viewAdvancedFilters')}>{label}</Label>
      <StyledSelectableSearchDropdown
        saveAsOption
        hideSelectAll
        values={values[unionStatusName]}
        onChange={handleUnionStatusChange}
      >
        {_map(unionStatusOptions, (item) => (
          <SelectableSearchDropdown.Option
            key={item.value}
            value={item.value}
            label={item.label}
            disabled={
              // do not disable SAG-AFTRA Eligible and SAG-AFTRA FiCore even if Non-Union is checked
              !_includes([...sagAftraChildrenExclusion, TALENT_UNION.NON_UNION], item.value) &&
              shouldNonUnionBeChecked(values[unionStatusName])
            }
          />
        ))}
      </StyledSelectableSearchDropdown>
    </>
  );
};

export default memo(UnionStatus);
